'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/ui/data-table';
import { Trash2, Edit2, Check, X, Package } from 'lucide-react';
import type { POItemFormState } from '@/types/purchase-order-form';

interface POItemsTableProps {
  items: POItemFormState[];
  onItemsChange: (items: POItemFormState[]) => void;
}

export function POItemsTable({ items, onItemsChange }: POItemsTableProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editQty, setEditQty] = useState<number>(0);
  const [editPrice, setEditPrice] = useState<number>(0);

  const totalAmount = items.reduce((sum, item) => sum + item.line_total, 0);

  const handleRemove = (itemId: string) => {
    onItemsChange(items.filter(item => item.item_id !== itemId));
  };

  const startEdit = (item: POItemFormState) => {
    setEditingId(item.item_id);
    setEditQty(item.quantity_ordered);
    setEditPrice(item.unit_price);
  };

  const saveEdit = (itemId: string) => {
    const updatedItems = items.map(item => {
      if (item.item_id === itemId) {
        const lineTotal = editQty * editPrice;
        return {
          ...item,
          quantity_ordered: editQty,
          unit_price: editPrice,
          line_total: lineTotal,
        };
      }
      return item;
    });
    onItemsChange(updatedItems);
    setEditingId(null);
    setEditQty(0);
    setEditPrice(0);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditQty(0);
    setEditPrice(0);
  };

  // Table columns
  const columns = [
    {
      key: 'item_code',
      header: 'Code',
      render: (item: POItemFormState) => (
        <span className="font-mono text-sm">{item.item_code}</span>
      ),
    },
    {
      key: 'item_name',
      header: 'Description',
      render: (item: POItemFormState) => (
        <span className="font-medium max-w-xs truncate" title={item.item_name}>
          {item.item_name}
        </span>
      ),
    },
    {
      key: 'uom',
      header: 'UOM',
      render: (item: POItemFormState) => (
        <span className="text-sm text-gray-600">{item.unit_of_measure}</span>
      ),
    },
    {
      key: 'quantity',
      header: 'Qty Ordered',
      render: (item: POItemFormState) => {
        if (editingId === item.item_id) {
          return (
            <Input
              type="number"
              min={1}
              value={editQty}
              onChange={(e) => setEditQty(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-20"
            />
          );
        }
        return <span className="font-mono">{item.quantity_ordered}</span>;
      },
    },
    {
      key: 'unit_price',
      header: 'Unit Price',
      render: (item: POItemFormState) => {
        if (editingId === item.item_id) {
          return (
            <Input
              type="number"
              step="0.01"
              min="0"
              value={editPrice}
              onChange={(e) => setEditPrice(parseFloat(e.target.value) || 0)}
              className="w-24"
            />
          );
        }
        return <span className="font-mono">${item.unit_price.toFixed(2)}</span>;
      },
    },
    {
      key: 'line_total',
      header: 'Total',
      render: (item: POItemFormState) => {
        const currentTotal = editingId === item.item_id ? editQty * editPrice : item.line_total;
        return (
          <span className="font-mono font-semibold">
            ${currentTotal.toFixed(2)}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (item: POItemFormState) => (
        <div className="flex items-center gap-1">
          {editingId === item.item_id ? (
            <>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => saveEdit(item.item_id)}
                className="h-8 w-8"
              >
                <Check className="h-4 w-4 text-green-600" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={cancelEdit}
                className="h-8 w-8"
              >
                <X className="h-4 w-4 text-red-600" />
              </Button>
            </>
          ) : (
            <>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => startEdit(item)}
                className="h-8 w-8"
              >
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => handleRemove(item.item_id)}
                className="h-8 w-8"
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {items.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <DataTable
            columns={columns}
            data={items}
            getItemId={(item) => item.item_id}
          />
        </div>
      )}

      {items.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <Package className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 mb-1">No items added yet</p>
          <p className="text-sm text-gray-500">Add items to create the purchase order</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="border-t pt-4 bg-gray-50 rounded-b-lg">
          <div className="flex items-center justify-end gap-4">
            <div className="text-right">
              <p className="text-sm text-gray-600">Total Amount</p>
              <p className="text-2xl font-mono font-bold text-primary">
                ${totalAmount.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
